import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { ChevronRight, TriangleAlert, Loader2, Info } from 'lucide-react';
import { useAppContext } from '../../../context/AppContext';
import { PERIODS, periodLabel } from '../../../utils/financialPeriods';
import { secondaryService } from '../../../services/secondaryService';
import MantleGrid, { MantleGridButton } from '../../../components/MantleGrid';
import { cell, GRID } from '../../../styles/gridStyles';
import PostingSummary from '../../../components/PostingSummary';

// =============================================================================
// SecondaryBudget — the shared body of the two budget pages.
//
// Customer Delivery and Store Replenishment are the same engine seen from two
// ends of the business, so they are one component with a `kind` and different
// wording. What differs is real but small: stores carry a temperature stream,
// cages instead of pallets, and an audience who must never see the other page.
//
// The point of the page is that there is almost nothing to capture. Every
// number comes from the setup page by way of vw_bud_delivery_cost:
//
//     volume -> drops -> (per carrier) share -> runs -> km -> cost
//
// The manager's job here is not data entry. It is to LOOK AT the derived
// drivers and argue with them: is 4.2 pallets a drop right? Is six drops a run
// still true on that route? The diagnostics are on the page for that reason.
//
// DRIVERS: DERIVED | MANUAL flips every lane in scope at once, the same way
// the inter-depot page switches volume source — so there is never a screen
// where half the numbers mean something different from the other half.
//
// CARRIERS AND RATES ARE READ-ONLY HERE. They belong to Route to Market >
// Secondary Lanes & Rates: the manager owns the drivers, the rate card is its
// own decision with its own audit trail.
// =============================================================================

const GRAINS = [
  { id: 'zone', label: 'Customer × zone' },
  { id: 'customer', label: 'Customer' },
  { id: 'channel', label: 'Channel' },
];
const RATE_BASIS_LABEL = {
  PER_DROP: { label: 'Per drop', driver: 'drops' },
  PER_UNIT: { label: 'Per load unit', driver: 'volume' },
  PER_RUN: { label: 'Per run', driver: 'runs' },
  PER_KM: { label: 'Per km', driver: 'km' },
};
const H_SCROLLBAR = 14;

const sum12 = (a) => (a || []).reduce((x, v) => x + (Number(v) || 0), 0);
const fmtR = (n, dp = 0) => `R ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
const fmtN = (n, dp = 0) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
const streamLabel = (s) => (s ? `${s[0]}${s.slice(1).toLowerCase()}` : '');

const SecondaryBudget = ({
  kind,                    // 'CUSTOMER' | 'STORE'
  title,
  blurb,
  serveTitle,
  serveLabel,
  siteLabel,
  storageKey,
}) => {
  const { selectedVersion, selectedSite, fyStartMonth } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  const [lanes, setLanes] = useState([]);
  const [cts, setCts] = useState([]);
  const [grain, setGrain] = useState('zone');
  const [collapsed, setCollapsed] = useState({});
  const [selRow, setSelRow] = useState(null);
  const [skus, setSkus] = useState([]);
  const [skuLoading, setSkuLoading] = useState(false);

  // Local drop edits, keyed `${laneId}|${period}`. Held until Save so a
  // twelve-cell paste is one round trip rather than twelve.
  const [edits, setEdits] = useState({});
  const dirty = Object.keys(edits).length > 0;

  // Every lane in scope shares one source, so the toggle reads off the first.
  const manual = lanes.length > 0 && lanes.every((l) => l.volume_source === 'MANUAL');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [budget, serve] = await Promise.all([
        secondaryService.getBudget(kind),
        secondaryService.getCostToServe(kind, grain),
      ]);
      setLanes(budget.data?.lanes || []);
      setCts(serve.data?.rows || []);
      setEdits({});
    } catch (e) {
      const status = e.response?.status;
      setError(status === 403
        ? 'Store replenishment is not enabled for this budget version. Turn it on from Secondary Lanes & Rates.'
        : (e.response?.data?.message || e.message || 'Could not load the secondary distribution budget.'));
      setLanes([]);
      setCts([]);
    } finally {
      setLoading(false);
    }
  }, [kind, grain]);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion, selectedSite]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const run = async (fn, msg) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
      if (msg) setToast(msg);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'That did not save.');
    } finally {
      setBusy(false);
    }
  };

  // A lane's drops with any unsaved edit folded in. Runs and km still show the
  // SERVER's numbers until save — they are rounded per carrier by the cost
  // view, and guessing that in the browser is how the two drift apart.
  const dropsOf = useCallback((lane) => {
    const base = lane.drivers?.drops || Array(12).fill(0);
    if (!manual) return base;
    return base.map((d, i) => {
      const e = edits[`${lane.lane_id}|${i}`];
      return e === undefined ? Number(d) || 0 : e;
    });
  }, [manual, edits]);

  const laneTotals = useMemo(() => lanes.map((l) => {
    const total = PERIODS.map((_, i) => (l.carriers || []).reduce((a, c) => a
      + (c.rates || []).reduce((b, r) => b + (Number(r.cost?.[i]) || 0), 0), 0));
    return { laneId: l.lane_id, total };
  }), [lanes]);

  const colTotals = useMemo(
    () => PERIODS.map((_, i) => laneTotals.reduce((a, t) => a + t.total[i], 0)),
    [laneTotals]
  );
  const grand = sum12(colTotals);

  // ---- The build grid --------------------------------------------------------
  const gridColumns = useMemo(() => ([
    { columnId: 'row', width: 330, resizable: true },
    ...PERIODS.map((p) => ({ columnId: `P${p}`, width: 82, resizable: true })),
    { columnId: 'annual', width: 120, resizable: true },
  ]), []);

  const gridRows = useMemo(() => {
    const out = [{
      rowId: 'header', height: GRID.size.headerRow,
      cells: [
        cell.header('Lane · Driver · Carrier · Rate'),
        ...PERIODS.map((p) => cell.header(periodLabel(p, fyStartMonth), { align: 'right' })),
        cell.header('Annual', { align: 'right' }),
      ],
    }];
    const numRow = (id, label, s, idx, { editable = false, agg = 'quarter', avg = false } = {}) => ({
      rowId: id, height: GRID.size.dataRow,
      cells: [
        cell.label(label, { idx, bold: false, align: 'right' }),
        ...s.map((v) => cell.number(avg ? Math.round(v * 10) / 10 : Math.round(v), { editable, idx })),
        cell.aggregate(avg
          ? Math.round((s.reduce((a, v) => a + v, 0) / (s.length || 1)) * 10) / 10
          : Math.round(sum12(s)), { kind: agg }),
      ],
    });

    lanes.forEach((lane, idx) => {
      const name = `${lane.origin_site_code} → ${lane.zone_name}${lane.stream ? ` · ${streamLabel(lane.stream)}` : ''}`;
      const unit = (lane.load_uom || 'PALLET').toLowerCase();
      const total = laneTotals.find((t) => t.laneId === lane.lane_id)?.total || Array(12).fill(0);
      const volume = (lane.drivers?.volume || Array(12).fill(0)).map(Number);
      const drops = dropsOf(lane).map(Number);

      if (collapsed[lane.lane_id]) {
        out.push({
          rowId: `${lane.lane_id}::summary`, height: GRID.size.dataRow,
          cells: [
            cell.label(`▸ ${name}`, { idx }),
            ...total.map((v) => cell.number(Math.round(v), { editable: false, idx })),
            cell.aggregate(Math.round(sum12(total)), { kind: 'year' }),
          ],
        });
        return;
      }

      out.push({
        rowId: `${lane.lane_id}::head`, height: GRID.size.dataRow,
        cells: [cell.label(`▾ ${name}`, { idx }), ...PERIODS.map(() => cell.text('', { idx })), cell.text('', { idx })],
      });
      out.push(numRow(`${lane.lane_id}::vol`, `Volume (${unit}s) — from forecast`, volume, idx));
      out.push(numRow(`${lane.lane_id}::drops`, manual ? 'Drops — manual' : 'Drops — derived', drops, idx, { editable: manual }));
      out.push(numRow(`${lane.lane_id}::avg`, `Avg drop size (${unit}s)`, drops.map((d, i) => (d > 0 ? volume[i] / d : 0)), idx, { avg: true }));

      (lane.carriers || []).forEach((c) => {
        const who = c.carrier_type === 'OWNED' ? 'Own fleet' : (c.contractor_name || c.contractor_code);
        const cd = c.drivers || {};
        const hasKm = (c.rates || []).some((r) => r.rate_basis === 'PER_KM');
        out.push({
          rowId: `${lane.lane_id}::c::${c.carrier_id}`, height: GRID.size.dataRow,
          cells: [
            cell.label(`${who} · ${fmtN(c.allocation_pct)}%${c.vehicle_name ? ` · ${c.vehicle_name}` : ''}`, { idx, bold: true, align: 'right' }),
            ...PERIODS.map(() => cell.text('', { idx })), cell.text('', { idx }),
          ],
        });
        out.push(numRow(`${lane.lane_id}::c::${c.carrier_id}::runs`, `Runs (÷ ${fmtN(lane.drops_per_run, 1)} drops/run)`, (cd.runs || Array(12).fill(0)).map(Number), idx));
        if (hasKm) out.push(numRow(`${lane.lane_id}::c::${c.carrier_id}::km`, `Km (× ${fmtN(lane.round_trip_km)} round trip)`, (cd.km || Array(12).fill(0)).map(Number), idx));

        (c.rates || []).forEach((r) => {
          const basis = RATE_BASIS_LABEL[r.rate_basis] || { label: r.rate_basis };
          const rate0 = Number(r.rate_amount?.[0]) || 0;
          out.push(numRow(
            `${lane.lane_id}::c::${c.carrier_id}::r::${r.rate_line_id}`,
            `${r.label || basis.label} @ ${fmtR(rate0, 2)}`,
            (r.cost || Array(12).fill(0)).map(Number), idx
          ));
        });
        if ((c.rates || []).length > 1) {
          out.push(numRow(`${lane.lane_id}::c::${c.carrier_id}::tot`, `${who} subtotal`,
            PERIODS.map((_, i) => (c.rates || []).reduce((a, r) => a + (Number(r.cost?.[i]) || 0), 0)), idx));
        }
      });

      out.push(numRow(`${lane.lane_id}::total`, 'Lane cost', total, idx, { agg: 'year' }));
    });

    out.push({
      rowId: 'totals', height: GRID.size.totalRow,
      cells: [
        cell.totalLabel('TOTAL'),
        ...colTotals.map((t) => cell.totalNumber(Math.round(t))),
        cell.totalNumber(Math.round(grand)),
      ],
    });
    return out;
  }, [lanes, laneTotals, collapsed, colTotals, grand, fyStartMonth, manual, dropsOf]);

  const gridHeight = useMemo(() => {
    const rows = lanes.reduce((a, l) => {
      if (collapsed[l.lane_id]) return a + 1;
      const carrierRows = (l.carriers || []).reduce((x, c) => x
        + 2
        + ((c.rates || []).some((r) => r.rate_basis === 'PER_KM') ? 1 : 0)
        + (c.rates || []).length
        + ((c.rates || []).length > 1 ? 1 : 0), 0);
      return a + 4 + carrierRows + 1;
    }, 0);
    return GRID.size.headerRow + Math.max(1, rows) * GRID.size.dataRow + GRID.size.totalRow + 44 + H_SCROLLBAR;
  }, [lanes, collapsed]);

  const toggle = (id) => setCollapsed((p) => ({ ...p, [id]: !p[id] }));
  const handleFocus = (loc) => {
    const rowId = String(loc.rowId);
    if (String(loc.columnId) === 'row' && (rowId.endsWith('::summary') || rowId.endsWith('::head'))) {
      toggle(rowId.split('::')[0]);
      return false;
    }
    return true;
  };
  const handleChanges = (changes) => {
    if (!manual) return;
    setEdits((prev) => {
      const next = { ...prev };
      changes.forEach((ch) => {
        const col = String(ch.columnId);
        if (!col.startsWith('P')) return;
        const parts = String(ch.rowId).split('::');
        if (parts[1] !== 'drops') return;
        const i = Number(col.slice(1)) - 1;
        const val = Number(ch.newCell?.value);
        if (Number.isNaN(val) || val < 0 || i < 0 || i > 11) return;
        next[`${parts[0]}|${i}`] = val;
      });
      return next;
    });
  };

  // Save writes one call per touched LANE, not per cell — the endpoint takes
  // twelve periods and treats a zero as "delete the override", so a lane the
  // user edited back to its derived value stops counting as typed.
  const saveEdits = () => run(async () => {
    const byLane = new Map();
    Object.entries(edits).forEach(([k, v]) => {
      const [laneId, i] = k.split('|');
      if (!byLane.has(laneId)) byLane.set(laneId, {});
      byLane.get(laneId)[Number(i)] = v;
    });
    for (const [laneId, cells] of byLane) {
      const lane = lanes.find((l) => l.lane_id === laneId);
      const base = lane?.drivers?.drops || Array(12).fill(0);
      const quantities = PERIODS.map((_, i) => (cells[i] === undefined ? Number(base[i]) || 0 : cells[i]));
      // eslint-disable-next-line no-await-in-loop
      await secondaryService.saveManualDrivers(laneId, 'DROPS', quantities);
    }
  }, 'Drops saved.');

  const setManual = (value) => run(
    () => secondaryService.setVolumeSource(value ? 'MANUAL' : 'FORECAST', kind),
    value ? 'Drivers switched to manual.' : 'Drivers back on the forecast.'
  );

  // ---- Cost to serve ---------------------------------------------------------
  const ctsRows = useMemo(() => cts.map((r) => {
    const vol = sum12(r.volume);
    const cost = sum12(r.cost);
    const drops = sum12(r.drops);
    return {
      ...r,
      label: r.zone_code
        ? `${r.member_name}${r.stream ? ` · ${streamLabel(r.stream)}` : ''}`
        : r.member_name,
      vol, drops, total: cost,
      perUnit: vol > 0 ? cost / vol : 0,
      avgDrop: drops > 0 ? vol / drops : 0,
    };
  }).sort((a, b) => b.perUnit - a.perUnit), [cts]);

  const ctsGrand = useMemo(() => ctsRows.reduce((a, r) => a + r.total, 0), [ctsRows]);
  const cheapest = ctsRows.length ? ctsRows[ctsRows.length - 1].perUnit : 0;

  const openSku = async (row) => {
    if (selRow === row.key) { setSelRow(null); return; }
    setSelRow(row.key);
    setSkus([]);
    setSkuLoading(true);
    try {
      const res = await secondaryService.getCostToServeSku({
        member: row.member_code, zone: row.zone_code, stream: row.stream,
      });
      setSkus(res.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the SKU build.');
    } finally {
      setSkuLoading(false);
    }
  };

  const postingEntries = useMemo(() => lanes.flatMap((l) => (l.carriers || []).flatMap((c) => (c.rates || []).map((r) => ({
    site: l.origin_site_code,
    gl: r.posting?.account_code || '',
    glName: r.posting?.account_description || '',
    costCentre: r.posting?.cost_center_code || '',
    values: (r.cost || []).map(Number),
  })))), [lanes]);

  const seg = (on) => `px-2.5 py-1.5 text-xs rounded border transition-colors disabled:opacity-40 ${on
    ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
    : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'}`;

  const totalDrops = lanes.reduce((a, l) => a + sum12(dropsOf(l)), 0);
  const totalRuns = lanes.reduce((a, l) => a + (l.carriers || []).reduce((x, c) => x + sum12(c.drivers?.runs), 0), 0);
  const overrides = lanes.reduce((a, l) => a + (l.manual?.drops || []).filter((v) => Number(v) > 0).length, 0);

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-3">
        <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{title}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{blurb}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">Drivers</span>
          <button disabled={busy || loading} onClick={() => setManual(false)} className={seg(!manual)}>Derived</button>
          <button disabled={busy || loading} onClick={() => setManual(true)} className={seg(manual)}>Manual</button>
          <span className="ml-2 text-[11px] text-slate-400">Carriers and rates are set on Route to Market → Secondary Lanes &amp; Rates.</span>
          {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-5">
        {error && (
          <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        {toast && (
          <div className="rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
            {toast}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400 py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : lanes.length === 0 && !error ? (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-8 text-center">
            <Info className="w-5 h-5 mx-auto mb-2 text-slate-400" />
            <p className="m-0 text-sm font-medium text-slate-700 dark:text-slate-200">No lanes yet.</p>
            <p className="m-0 mt-1 text-xs text-slate-500 dark:text-slate-400">
              Create the zones and lanes on Route to Market → Secondary Lanes &amp; Rates, then everything here derives itself.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                ['Budget / year', fmtR(grand)],
                ['Drops / year', fmtN(totalDrops)],
                ['Runs / year', fmtN(totalRuns)],
                ['Numbers typed', manual ? `${overrides} override${overrides === 1 ? '' : 's'}` : '0'],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2.5">
                  <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</span>
                  <span className="block mt-0.5 font-mono text-lg font-bold text-slate-800 dark:text-slate-100">{value}</span>
                </div>
              ))}
            </div>

            <div>
              <p className="m-0 mb-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Driver Build &amp; Cost by Period</p>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" style={{ height: gridHeight }}>
                <MantleGrid
                  rows={gridRows} columns={gridColumns}
                  onCellsChanged={handleChanges} onFocusLocationChanging={handleFocus}
                  hint={manual
                    ? 'Manual drivers — type drops, then Save. Runs, km and cost recompute server-side, rounded per carrier.'
                    : 'Everything derived. Click a lane name to expand or collapse. Switch Drivers to Manual to override a lane the formula does not fit.'}
                  toolbar={<>
                    <MantleGridButton onClick={() => setCollapsed({})}>Expand all</MantleGridButton>
                    <MantleGridButton onClick={() => setCollapsed(Object.fromEntries(lanes.map((l) => [l.lane_id, true])))}>Collapse all</MantleGridButton>
                    <MantleGridButton onClick={() => setEdits({})} disabled={!dirty}>Discard</MantleGridButton>
                    <MantleGridButton accent={dirty} onClick={saveEdits} disabled={!dirty || busy}>Save</MantleGridButton>
                  </>}
                />
              </div>
              <p className="m-0 mt-1.5 text-[11px] text-slate-400">
                Drops are a <strong>lane</strong> total; runs are <strong>per carrier</strong> — a carrier moving 40% of a lane runs its own trucks, so it rounds up on its own share.
                Volume ÷ drops = average drop size, and both it and drops-per-run are assumptions worth challenging.
              </p>
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                <p className="m-0 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  {serveTitle} <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold rounded bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400">derived — nothing typed</span>
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 dark:text-slate-400">Grain</span>
                  {GRAINS.map((g) => (
                    <button key={g.id} disabled={busy} onClick={() => { setGrain(g.id); setSelRow(null); }} className={seg(grain === g.id)}>{g.label}</button>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
                <table className="w-full border-collapse text-xs">
                  <thead><tr className="bg-slate-100 dark:bg-slate-700/60 text-left">
                    {['', serveLabel, 'Volume', 'Drops', 'Avg Drop', 'Cost / yr', '% of total', 'Cost / Unit', 'vs cheapest'].map((h) => (
                      <th key={h} className="px-2 py-2 font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase text-[10px] whitespace-nowrap">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {ctsRows.length === 0 && (
                      <tr><td colSpan={9} className="px-2 py-6 text-center text-slate-400">
                        Nothing to allocate yet — assign customers to a zone on Secondary Lanes &amp; Rates.
                      </td></tr>
                    )}
                    {ctsRows.map((r, idx) => {
                      const open = selRow === r.key;
                      const ratio = cheapest > 0 ? r.perUnit / cheapest : 1;
                      const uom = (r.load_uom || 'PALLET').toLowerCase();
                      return (
                        <React.Fragment key={r.key}>
                          <tr onClick={() => openSku(r)}
                            className={`border-t border-slate-100 dark:border-slate-700/60 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 ${idx % 2 ? 'bg-slate-50/50 dark:bg-slate-800/40' : ''}`}>
                            <td className="px-2 py-1.5"><ChevronRight className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`} /></td>
                            <td className="px-2 py-1.5 font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">{r.label}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-500 dark:text-slate-400">{fmtN(r.vol)} <span className="text-[10px] text-slate-400">{uom}s</span></td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-500 dark:text-slate-400">{fmtN(r.drops)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-500 dark:text-slate-400">{fmtN(r.avgDrop, 1)}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{fmtR(r.total)}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-400">{fmtN(ctsGrand > 0 ? (r.total / ctsGrand) * 100 : 0, 1)}%</td>
                            <td className={`px-2 py-1.5 text-right font-mono font-bold whitespace-nowrap ${ratio >= 4 ? 'text-red-600 dark:text-red-400' : ratio >= 2 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-700 dark:text-slate-200'}`}>{fmtR(r.perUnit, 2)}</td>
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1.5">
                                <div className="h-1.5 w-16 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                  <div className={`h-full ${ratio >= 4 ? 'bg-red-500' : ratio >= 2 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${Math.min(100, (ratio / 12) * 100)}%` }} />
                                </div>
                                <span className="font-mono text-[10px] text-slate-400">{fmtN(ratio, 1)}×</span>
                              </div>
                            </td>
                          </tr>
                          {open && (
                            <tr className="border-t border-slate-100 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-900/40">
                              <td />
                              <td colSpan={8} className="px-2 py-3">
                                <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                  Cost per case by SKU — {fmtR(r.perUnit, 2)} per {uom} ÷ cases per {uom}
                                </p>
                                {skuLoading ? (
                                  <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</span>
                                ) : skus.length === 0 ? (
                                  <p className="m-0 text-[11px] text-slate-400">No forecast volume for this row, so there is nothing to divide.</p>
                                ) : (
                                  <table className="border-collapse text-xs">
                                    <thead><tr className="text-left">
                                      {['SKU', 'Description', `Cases / ${uom}`, 'Cases / yr', 'Cost / case'].map((h) => (
                                        <th key={h} className="px-2 py-1 font-bold tracking-wider text-slate-400 uppercase text-[10px] whitespace-nowrap">{h}</th>
                                      ))}
                                    </tr></thead>
                                    <tbody>
                                      {skus.map((s) => (
                                        <tr key={s.stock_code} className="border-t border-slate-200/70 dark:border-slate-700/50">
                                          <td className="px-2 py-1 font-mono text-slate-600 dark:text-slate-300">{s.stock_code}</td>
                                          <td className="px-2 py-1 text-slate-600 dark:text-slate-300 whitespace-nowrap">{s.description}</td>
                                          <td className="px-2 py-1 text-right font-mono text-slate-500 dark:text-slate-400">{fmtN(s.cases_per_unit, 1)}</td>
                                          <td className="px-2 py-1 text-right font-mono text-slate-500 dark:text-slate-400">{fmtN(s.cases_per_year)}</td>
                                          <td className="px-2 py-1 text-right font-mono font-semibold text-slate-700 dark:text-slate-200">{fmtR(s.cost_per_case, 2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                                <p className="m-0 mt-1.5 text-[11px] text-slate-400">
                                  Nothing SKU-level is stored. Cases-per-{uom} already carries the density difference — which is exactly why transport cost must not be allocated on cases.
                                </p>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot><tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/70">
                    <td colSpan={5} className="px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">Total</td>
                    <td className="px-2 py-2 text-right font-mono text-sm font-bold text-slate-800 dark:text-slate-100 whitespace-nowrap">{fmtR(ctsGrand)}</td>
                    <td colSpan={3} />
                  </tr></tfoot>
                </table>
              </div>
              <p className="m-0 mt-1.5 flex items-start gap-1.5 text-[11px] text-slate-400">
                <TriangleAlert className="w-3.5 h-3.5 shrink-0 mt-px text-amber-500" />
                Click a row for the SKU build. Each rate component allocates on <strong>its own base</strong> — per-drop and per-run cost on drops, per-unit cost on volume.
                Allocate everything on volume and every row in a zone lands on the same cost per unit, which is the one thing this page exists to disprove.
              </p>
            </div>

            <PostingSummary entries={postingEntries} siteLabel={siteLabel} storageKey={storageKey} />
          </>
        )}
      </div>
    </div>
  );
};

export default SecondaryBudget;
